// C:\Users\cornn\source\repos\purehealthnexus\src\components\AiDashboard\AiMyNutritionStatus\AiUserLogs.jsx
// Dynamic consumption/suggested logs driven by the same JSON shape that
// AiMealApiCall.js / GeminiNutritionService returns (latestNutritionJson).


function toTitleCase(text) {
  if (typeof text !== 'string') return '';
  return text
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Build "Current Consumption" log from:
 * - latestMinimalJson (separator parsePreview)
 * - latestNutritionJson (Gemini normalized JSON: timestampIso, consumed[], request.text, ...)
 *
 * Shape of returned log:
 * [
 *   { title: string, time: string, items: string[] }
 * ]
 *
 * Priority for items:
 *  1) AI-normalized consumed[].label   (Plain Greek Yogurt, Fresh Blueberries, ...)
 *  2) separator parsePreview.parts     (fallback)
 *  3) raw request text                 (last resort)
 */
export function buildDynamicConsumptionLog(latestMinimalJson, latestNutritionJson) {
  const hasMinimal =
    latestMinimalJson && typeof latestMinimalJson === 'object';
  const hasNutrition =
    latestNutritionJson && typeof latestNutritionJson === 'object';

  // No data yet → no rows
  if (!hasMinimal && !hasNutrition) {
    return [];
  }

  // Prefer the AI timestamp; fall back to separator timestamp
  const timestampIso =
    (hasNutrition && latestNutritionJson.timestampIso) ||
    (hasMinimal && latestMinimalJson.timestampIso) ||
    null;

  let timeDescription = 'Approx. unknown time';
  if (timestampIso) {
    try {
      const parsedDate = new Date(timestampIso);
      const timeString = parsedDate.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      });
      timeDescription = `Approx. ${timeString}`;
    } catch {
      // keep default
    }
  }

  const parseParts = Array.isArray(latestMinimalJson?.parsePreview?.parts)
    ? latestMinimalJson.parsePreview.parts
    : null;

  let items = [];

  // 1) Prefer AI "consumed" labels (normalized food names that power "About to eat..." cards)
  const consumedArray =
    hasNutrition && Array.isArray(latestNutritionJson.consumed)
      ? latestNutritionJson.consumed
      : [];

  if (consumedArray.length > 0) {
    // Flatten all consumed items and their sub-items into a single list
    items = consumedArray.flatMap((item, index) => {
      // If the consumed item has an "items" array with multiple entries, use those
      if (Array.isArray(item?.items) && item.items.length > 0) {
        return item.items.map((subItem) => toTitleCase(String(subItem).trim()));
      }

      // Otherwise, use the main label
      if (item && typeof item.label === 'string' && item.label.trim()) {
        return [toTitleCase(item.label.trim())];
      }

      // Fallback: parsePreview part at same index
      if (parseParts && parseParts[index]) {
        return [toTitleCase(String(parseParts[index]).trim())];
      }

      // Last resort
      return [`Item ${index + 1}`];
    });
  }
  // 2) Fallback: separator parsePreview.parts (title-cased)
  else if (parseParts && parseParts.length > 0) {
    items = parseParts.map((part) => toTitleCase(String(part)));
  }
  // 3) Else, fall back to raw request text
  else if (hasNutrition && latestNutritionJson.request?.text) {
    items = [String(latestNutritionJson.request.text)];
  }

  if (items.length === 0) {
    return [];
  }

  return [
    {
      title: 'Latest meal',
      time: timeDescription,
      items,
    },
  ];
}


export function buildDynamicSuggestedLog(latestMinimalJson, latestNutritionJson) {
  void latestMinimalJson;
  void latestNutritionJson;
  return [];
}
